import React, {
  useState,
  useContext,
  useEffect,
  useMemo,
  useTransition,
} from "react";
import {
  Box,
  Typography,
  Paper,
  Checkbox,
  Stack,
  CircularProgress,
  IconButton,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useNavigate } from "react-router-dom";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";

import GeneralContext from "../GeneralContext/GeneralContext";
import InputFilter from "../GeneralComponents/InputFilter";
import InputChipSelect from "../GeneralComponents/InputChipSelect";
import PageBody from "../GeneralComponents/PageBody";
import PageButtonBlack from "../GeneralComponents/PageButtonBlack";
import PageButtonWhite from "../GeneralComponents/PageButtonWhite";
import PageBgBlock from "../GeneralComponents/PageBgBlock";
import ListEmpty from "../GeneralComponents/ListEmpty";
import ModalMassive from "../GeneralComponents/ModalMassive";
import axios from "../../api/axios";
import * as XLSX from "xlsx";
import ContingenciasHiddenPreview from "./ContingenciasHiddenPreview";

import {
  NO_EES,
  NO_EES_TEMP,
  NO_SUBTEL,
  NO_MINEDUC,
  REJECTED,
  APROVED,
  ROL_ADMIN,
  ROL_OP_ADMIN,
  ROL_REP_EES,
  ROL_REP_EES_TEMP,
  ROL_ADMIN_SUBTEL,
  ROL_VAL_SUBTEL,
  ROL_CON_MINEDUC,
  ROL_MINEDUC_ADMIN,
} from "../../Constants/Constants";

import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);

const formatPeriodos = (arr = []) => {
  if (arr.length <= 2) return arr.join(", ");
  return `${arr.slice(0, 2).join(", ")} y ${arr.length - 2} más`;
};

const parseDate = (str) =>
  str
    ? dayjs(str, ["YYYY-MM-DD", "YYYY/MM/DD", "YYYY-MM-DDTHH:mm:ss"]).startOf(
        "day"
      )
    : null;

/** Devuelve ['YYYY-MM', …] para todos los meses entre start y end (ambos inclusive). */
const monthsBetween = (startStr, endStr) => {
  const out = [];
  let start = parseDate(startStr)?.startOf("month");
  const end = parseDate(endStr)?.startOf("month");
  if (!start || !end) return out;

  while (start.isSame(end) || start.isBefore(end)) {
    out.push(start.format("YYYY-MM"));
    start = start.add(1, "month");
  }
  return out;
};

/** Clasifica el periodo en Rojo / Amarillo / Verde según antigüedad. */
const getPeriodoStatus = (periodos = []) => {
  if (!periodos || periodos.length === 0) return "Verde";
  const last = periodos[periodos.length - 1];
  const str = (last || "").slice(0, 7); // 'YYYY-MM'
  const periodoDate = dayjs(str, "YYYY-MM", true);
  if (!periodoDate.isValid()) return "Verde";

  const now = dayjs().startOf("month");
  const diff = now.diff(periodoDate, "month");

  if (diff >= 2) return "Rojo";
  if (diff === 1) return "Amarillo";
  return "Verde";
};

const getColorFromStatus = (status) => {
  if (status === "Rojo") return "#FB3B3B";
  if (status === "Amarillo") return "#FFE263";
  return "#41AE43";
};

/** Cuenta cuántos Rojo / Amarillo / Verde hay en un arreglo de filas. */
const countSemaforo = (rows) => {
  const acc = { Rojo: 0, Amarillo: 0, Verde: 0 };
  rows.forEach((r) => {
    if (r.semaforo === "Rojo") acc.Rojo += 1;
    else if (r.semaforo === "Amarillo") acc.Amarillo += 1;
    else if (r.semaforo === "Verde") acc.Verde += 1;
  });
  return acc;
};

/** Aplica el objeto de filtros sobre un arreglo de filas base. */
const filterRows = (baseRows, filterObj) => {
  if (!filterObj || Object.keys(filterObj).length === 0) return baseRows;

  return baseRows.filter((item) =>
    Object.entries(filterObj).every(([key, val]) => {
      if (key === "periodo") {
        return item.periodos?.includes(val);
      }

      if ((key === "folio" || key === "rbd") && Array.isArray(val)) {
        if (key === "folio") {
          return val.includes(item.folio);
        } else {
          return val.includes(item.rbd);
        }
      }

      if (key === "semaforo") {
        // permitir que sea string o array
        if (Array.isArray(val)) return val.includes(item.semaforo);
        return item.semaforo === val;
      }

      return item[key] === val;
    })
  );
};

export default function ContingenciasPendientesList({ prefilter = false }) {
  const [rows, setRows] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [filters, setFilters] = useState({});
  const [loadingTable, setLoadingTable] = useState(true);
  const [selected, setSelected] = useState([]);
  const [downloadLoader, setDownloadLoader] = useState(false);
  const [selectedPeriodo, setSelectedPeriodo] = useState(null);
  const [selectedPeriodoData, setSelectedPeriodoData] = useState([]);

  const [currentRow, setCurrentRow] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [isPending, startTransition] = useTransition();

  const [action, setAction] = useState("");
  const [open, setOpen] = useState(false);

  // resumen semáforo
  const [globalSemCounts, setGlobalSemCounts] = useState({
    Rojo: 0,
    Amarillo: 0,
    Verde: 0,
  });
  const [filteredSemCounts, setFilteredSemCounts] = useState({
    Rojo: 0,
    Amarillo: 0,
    Verde: 0,
  });

  const SEMAFORO_UI = {
  Rojo: { label: "Críticas", bg: "#F6C9C9", border: "#E7A2A2" },
  Amarillo: { label: "Por vencer", bg: "#FFF1B8", border: "#E6D27A" },
  Verde: { label: "En plazo", bg: "#CFEFD0", border: "#9ED49F" },
};

const getVencePlazo = (status) => {
  const offset = status === "Rojo" ? 0 : status === "Amarillo" ? 1 : 2;
  return dayjs().add(offset, "month").endOf("month").format("DD-MM-YYYY");
};

const handleSemaforoButton = (status) => {
  const nextValue = filters.semaforo === status ? null : status;
  filterRecords("semaforo", nextValue);
};

  const [defaultSemaforoApplied, setDefaultSemaforoApplied] = useState(false);

  const navigate = useNavigate();
  const { includeRol, pageTitle, setPageTitle, userSesion } =
    useContext(GeneralContext);

  const firmasPendientes = [NO_EES, NO_EES_TEMP, NO_SUBTEL, NO_MINEDUC];

  const rolesFirma = {
    EES: [NO_EES, NO_EES_TEMP],
    SUBTEL: [NO_SUBTEL],
    MINEDUC: [NO_MINEDUC],
  };

  const hasActionableSelection = useMemo(() => {
    if (!selected?.length) return false;
    const selectedRows = tableData.filter((r) => selected.includes(r.id));
    return selectedRows.some((r) => firmasPendientes.includes(r.estado));
  }, [selected, tableData]);

  const handleOpen = (actionName) => {
    setAction(actionName);
    setOpen(true);
  };

  const handleClose = (fromModal, successfulCount) => {
    setOpen(false);
    fetchData(fromModal, successfulCount);
  };

  function getSignatureText(currentState) {
    let inst = "FAS";
    if (includeRol([ROL_REP_EES, ROL_REP_EES_TEMP])) {
      inst = "EES";
    } else if (includeRol([ROL_ADMIN_SUBTEL, ROL_VAL_SUBTEL])) {
      inst = "SUBTEL";
    } else if (includeRol([ROL_MINEDUC_ADMIN, ROL_CON_MINEDUC])) {
      inst = "MINEDUC";
    }

    const currentStep = firmasPendientes.indexOf(currentState);

    const userSteps = rolesFirma[inst] || [];

    const userStepsIndex = firmasPendientes.findIndex((etapa) =>
      userSteps.includes(etapa)
    );
    if (userSteps.includes(currentState)) {
      return "Firmar";
    }

    if (userStepsIndex > currentStep) {
      return "Firmar";
    }
    return "Firmado";
  }

  const canSign = (estado) => {
    if (
      (estado === NO_EES || estado === NO_EES_TEMP) &&
      includeRol([ROL_REP_EES, ROL_REP_EES_TEMP])
    ) {
      return true;
    } else if (
      estado === NO_SUBTEL &&
      includeRol([ROL_ADMIN_SUBTEL, ROL_VAL_SUBTEL])
    ) {
      return true;
    } else if (
      estado === NO_MINEDUC &&
      includeRol([ROL_MINEDUC_ADMIN, ROL_CON_MINEDUC])
    ) {
      return true;
    }

    return false;
  };

  const downloadXLS = () => {
    const selectedRows = selectedPeriodoData;
    if (selectedRows.length === 0) {
      alert("No hay filas seleccionadas para descargar.");
      return;
    }

    const filteredData = selectedRows.map((row) => ({
      rbd: row.rbd,
      folio: row.folio,
      periodos: row.periodos.join(", "),
      tipo_contingencia: row.tipo_contingencia,
      id_operador: row.id_operador,
      operador: row.operador,
      rut_emisor: row.rut_emisor,
      id_zona: row.id_zona,
      estado: row.estado,
      estado_resumen: row.estado_resumen,
      fecha_creacion: row.fecha_creacion,
      fecha_inicio:
        row.tipo_contingencia === "PROGRAMADA"
          ? row.programada_inicio_contingencia + "T" + row.programada_hora_inicio
          : row.especifica_fecha_hora_ocurrencia,
      fecha_termino:
        row.tipo_contingencia === "PROGRAMADA"
          ? row.programada_fin_contingencia + "T" + row.programada_hora_termino
          : row.especifica_fecha_hora_resolucion,
      descripcion:
        row.tipo_contingencia === "PROGRAMADA"
          ? row.programada_descripcion
          : row.especifica_descripcion_falla,
      link_documento: row.ruta_url,
    }));

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Tabla");
    XLSX.writeFile(workbook, "tabla.xlsx");
  };

  const downloadPDF = (url) => {
    window.open(url, "_blank");
  };

  const downloadZip = async () => {
    try {
      if (selected.length === 0) {
        return;
      }
      setDownloadLoader(true);
      const now = new Date();
      const timestamp = `${now.getFullYear()}-${String(
        now.getMonth() + 1
      ).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}_${String(
        now.getHours()
      ).padStart(2, "0")}-${String(now.getMinutes()).padStart(
        2,
        "0"
      )}-${String(now.getSeconds()).padStart(2, "0")}`;

      const response = await axios.downloadDocuments("contingencias", selected);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `contingencias_${timestamp}.zip`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (error) {
      console.error("Error al descargar documentos:", error);
    }
    setDownloadLoader(false);
  };

  const fetchData = async (fromModal = false, count = 0) => {
    if (fromModal && count === 0) {
      return;
    }
    setLoadingTable(true);
    axios
      .getContingenciasPendientesData(userSesion.id)
      .then((response) => {
        const modifiedData = response.data
          .filter((row) => !prefilter || row.estado === "Pendiente")
          .map((row) => {
            const isProgramada = row.tipo_contingencia === "PROGRAMADA";

            const fechaInicio = isProgramada
              ? row.programada_inicio_contingencia
              : row.especifica_fecha_hora_ocurrencia;

            const fechaFin = isProgramada
              ? row.programada_fin_contingencia
              : row.especifica_fecha_hora_resolucion;

            const periodos = monthsBetween(fechaInicio, fechaFin);
            const semaforo = getPeriodoStatus(periodos);

            const origen =
              row.especifica_origen_falla ===
                "Falla interna al adjudicatario" ||
              row.tipo_contingencia === "PROGRAMADA"
                ? "Interna"
                : "Externa";

            return {
              ...row,
              periodo_creacion: row.fecha_creacion.slice(0, 7),
              rbd: row.rbd || "-",
              periodo: row.fecha_creacion.slice(0, 7),
              periodos,
              semaforo, 
              fecha: row.fecha_creacion.split("T")[0],
              estado_resumen:
                row.estado !== APROVED && row.estado !== REJECTED
                  ? "En Proceso"
                  : row.estado,
              puede_firmar: canSign(row.estado),
              firma: "",
              extension:
                row.tipo_contingencia === "ZONAL" ? "Por Zona" : "Por RBD",
              tipo: row.tipo_contingencia,
              origen: origen,
              cerrado_por: row.cerrado_por ? row.cerrado_por : "No cerrado",
              tipo_compuesto:
                row.tipo_contingencia === "ZONAL"
                  ? row.tipo_contingencia
                  : row.tipo_contingencia + " (" + origen + ")",
              folio: String(row.id).padStart(8, "0"),
            };
          });

        // actualizar totales globales
        const globalCounts = countSemaforo(modifiedData);
        setGlobalSemCounts(globalCounts);

        let newFilters = { ...filters };
        let newTable = modifiedData;

        // aplicar filtro semáforo por defecto sólo una vez
        if (!defaultSemaforoApplied) {
          let defaultSem = null;
          if (globalCounts.Rojo > 0) defaultSem = "Rojo";
          else if (globalCounts.Amarillo > 0) defaultSem = "Amarillo";
          else if (globalCounts.Verde > 0) defaultSem = "Verde";

          if (defaultSem) {
            newFilters = { ...newFilters, semaforo: defaultSem };
            newTable = filterRows(modifiedData, newFilters);
          }
          setDefaultSemaforoApplied(true);
        } else {
          newTable = filterRows(modifiedData, newFilters);
        }

        setFilters(newFilters);
        setRows(modifiedData);
        setTableData(newTable);
        setFilteredSemCounts(countSemaforo(newTable));

        // seleccionar por defecto el primer row si no hay uno ya seleccionado
        if (!currentRow && newTable.length > 0) {
          setCurrentRow(newTable[0]);
        }

        if (count !== 0) {
          // si viene desde el modal, ya aplicamos filtros arriba
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setLoadingTable(false);
      });
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setPageTitle("Listado de contingencias");
  }, [pageTitle, setPageTitle]);

  // cuando cambia currentRow, apagamos loader del preview
  useEffect(() => {
    if (!currentRow) return;
    setPreviewLoading(false);
  }, [currentRow]);

  const filterRecords = (name, value) => {
    const filterObj = { ...filters };
    filterObj[name] = value;

    const isEmpty =
      value === null || (Array.isArray(value) && value.length === 0);

    if (isEmpty) {
      delete filterObj[name];
    }

    // manejo especial para periodo (para el XLS)
    if (name === "periodo") {
      setSelectedPeriodo(value);

      const newPeriodoData =
        value != null
          ? rows.filter((item) => item.periodos?.includes(value))
          : [];

      setSelectedPeriodoData(newPeriodoData);
    }

    const newTableData = filterRows(rows, filterObj);

    if (JSON.stringify(newTableData) !== JSON.stringify(tableData)) {
      setSelected([]);
    }

    setFilters(filterObj);
    setTableData(newTableData);
    setFilteredSemCounts(countSemaforo(newTableData));
  };

  const handleCheckboxClick = (id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  const handleSelectAllLeft = (event) => {
    if (event.target.checked) {
      const allIds = tableData.map((r) => r.id);
      setSelected(allIds);
    } else {
      setSelected([]);
    }
  };

  const drawerWidth = 300;


  const isPreviewPending = previewLoading || isPending;

  const renderStat = (label, count, color) => (
    <Stack
      key={label}
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{ mr: 2 }}
    >
      <FiberManualRecordIcon sx={{ fontSize: "0.9rem", color }} />
      <Typography variant="body2">
        {label}: <strong>{count}</strong>
      </Typography>
    </Stack>
  );

  return (
    <PageBody>
      <Box width="100%">
        {/* Filtros superiores */}
        <Grid
          container
          spacing={1}
          sx={{ marginBottom: "28px", marginTop: "-2rem" }}
          alignItems="flex-start"
        >
          <Grid size={6}>
            <Grid container spacing={1}>
              <Grid size={6}>
                <InputFilter
                  data={rows}
                  item={"periodo"}
                  name={"Periodo Afectación"}
                  callback={filterRecords}
                />
              </Grid>
              <Grid size={6}>
                <InputFilter
                  data={rows}
                  item={"periodo_creacion"}
                  name={"Periodo creación"}
                  callback={filterRecords}
                />
              </Grid>

              {includeRol([
                ROL_MINEDUC_ADMIN,
                ROL_REP_EES,
                ROL_REP_EES_TEMP,
                ROL_CON_MINEDUC,
                ROL_VAL_SUBTEL,
                ROL_ADMIN,
                ROL_ADMIN_SUBTEL,
              ]) && (
                <Grid size={6}>
                  <InputFilter
                    data={rows}
                    item={"operador"}
                    name={"Operador"}
                    callback={filterRecords}
                  />
                </Grid>
              )}

              <Grid size={6}>
                <InputFilter
                  data={rows}
                  item={"tipo_compuesto"}
                  name={"Tipo"}
                  callback={filterRecords}
                />
              </Grid>

              <Grid size={6}>
                <InputFilter
                  data={rows}
                  item={"estado"}
                  name={"Estado"}
                  defaultOpt={[
                    "Falta Firma EES",
                    "Falta Firma EES Temporal",
                    "Falta firma SUBTEL",
                    "Falta firma MINEDUC",
                  ]}
                  callback={filterRecords}
                />
              </Grid>


            </Grid>
          </Grid>

          <Grid container size={6} spacing={0}>
            <Grid size={12}>
              <InputChipSelect
                data={rows}
                item={"folio"}
                name={"ID folio"}
                callback={filterRecords}
              />
            </Grid>

            {includeRol([
              ROL_MINEDUC_ADMIN,
              ROL_CON_MINEDUC,
              ROL_VAL_SUBTEL,
              ROL_ADMIN,
            ]) && (
              <Grid size={12}>
                <InputChipSelect
                  data={rows}
                  item={"rbd"}
                  name={"RBD"}
                  callback={filterRecords}
                />
              </Grid>
            )}
          </Grid>
        </Grid>

        {/* Título */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            justifyContent: "center",
            borderRadius: "0.25rem",
            marginBottom: "0.5rem",
          }}
        >
          <Typography
            variant="h1"
            color="inherit"
            noWrap
            sx={{ textAlign: "center", padding: "0.5rem" }}
          >
            PRIORIZACIÓN DE CONTINGENCIAS PENDIENTES
          </Typography>
        </Box>

       {/* Botonera de priorización (filtra por semáforo) */}
<Box sx={{ mb: 2 }}>
  <Grid container spacing={2} justifyContent="center">
    {(["Rojo", "Amarillo", "Verde"]).map((status) => {
      const ui = SEMAFORO_UI[status];
      const count = globalSemCounts[status] ?? 0;
      const active = filters.semaforo === status;

      return (
        <Grid key={status} size={{ xs: 12, sm: 4, md: 4 }}>
          <Paper
            role="button"
            tabIndex={0}
            onClick={() => handleSemaforoButton(status)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") handleSemaforoButton(status);
            }}
            elevation={0}
            sx={{
              cursor: "pointer",
              userSelect: "none",
              textAlign: "center",
              py: 1.6,
              px: 1.2,
              borderRadius: "6px",
              bgcolor: ui.bg,
              border: `2px solid ${active ? "#003B8D" : ui.border}`,
              outline: active ? "2px solid rgba(0,59,141,0.25)" : "none",
              transition: "all 120ms ease",
              "&:hover": { filter: "brightness(0.98)" },
            }}
          >
            <Typography sx={{ fontSize: "1.35rem", fontWeight: 800, lineHeight: 1 }}>
              {count} 
            </Typography>

            
            <Typography sx={{ fontSize: "0.78rem", mt: 0.4, opacity: 0.85 }}>
              (vence plazo: {getVencePlazo(status)})
            </Typography>


          </Paper>
        </Grid>
      );
    })}
  </Grid>
</Box>

                    {/* Botones inferiores */}
            {includeRol([
              ROL_VAL_SUBTEL,
              ROL_ADMIN,
              ROL_ADMIN_SUBTEL,
              ROL_MINEDUC_ADMIN,
            ]) ? (
              <Grid container spacing={2} columns={9} sx={{ my: 3 }}>
                <Grid size={2}>
                  <PageButtonBlack
                    loading={downloadLoader || !hasActionableSelection}
                    onClick={(e) => {
                      e.preventDefault();
                      handleOpen("Rechazar");
                    }}
                    width="100%"
                  >
                    Rechazar masivamente
                  </PageButtonBlack>
                </Grid>
                <Grid size={2}>
                  <PageButtonBlack
                    loading={downloadLoader || !hasActionableSelection}
                    onClick={(e) => {
                      e.preventDefault();
                      handleOpen("Aprobar");
                    }}
                    width="100%"
                  >
                    Aprobar masivamente
                  </PageButtonBlack>
                </Grid>

                <Grid size={2}>
                  <PageButtonWhite
                    loading={selectedPeriodoData.length === 0}
                    onClick={downloadXLS}
                    width="100%"
                  >
                    Descarga .xlsx periodo: {selectedPeriodo || "-"}
                  </PageButtonWhite>
                </Grid>

                <Grid size={3}>
                  <PageButtonWhite
                    loading={downloadLoader || selected.length === 0}
                    onClick={downloadZip}
                    width="100%"
                  >
                    Descarga selección de documentos (.zip)
                  </PageButtonWhite>
                </Grid>
              </Grid>
            ) : (
              <Grid container spacing={1} sx={{ mt: 2 }}>
                <Grid size={3} />

                <Grid size={4}>
                  <PageButtonWhite
                    loading={selectedPeriodoData.length === 0}
                    onClick={downloadXLS}
                    width="100%"
                  >
                    Descarga .xlsx periodo: {selectedPeriodo || "-"}
                  </PageButtonWhite>
                </Grid>

                <Grid size={5}>
                  <PageButtonWhite
                    loading={downloadLoader || selected.length === 0}
                    onClick={downloadZip}
                    width="100%"
                  >
                    Descarga selección de documentos (.zip)
                  </PageButtonWhite>
                </Grid>
              </Grid>
            )}



        {loadingTable ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              height: "50vh",
              width: "100%",
              backgroundColor: "white",
            }}
          >
            <CircularProgress />
            <Typography variant="h6" sx={{ marginTop: 2 }}>
              Cargando...
            </Typography>
          </Box>
        ) : (
          <>
            <PageBgBlock pad="0">
              {tableData.length === 0 ? (
                <ListEmpty />
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    alignItems: "stretch",
                    overflowX: "hidden",
                  }}
                >
                  {/* PANEL IZQUIERDO */}
                  <Paper
                    sx={{
                      flex: `0 0 ${drawerWidth}px`,
                      maxWidth: drawerWidth,
                      height: "70vh",
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: "4px",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        px: 1,
                        py: 1.5,
                        borderBottom: "1px solid #eee",
                      }}
                    >
                      <Checkbox
                        indeterminate={
                          selected.length > 0 &&
                          selected.length < tableData.length
                        }
                        checked={
                          tableData.length > 0 &&
                          selected.length === tableData.length
                        }
                        onChange={handleSelectAllLeft}
                      />
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 500, fontSize: "1.2rem" }}
                      >
                        Contingencias filtradas ({tableData.length})
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        flexGrow: 1,
                        overflowY: "auto",
                        py: 1,
                      }}
                    >
                      {tableData.map((row) => {
                        const checked = isSelected(row.id);
                        const active = currentRow && row.id === currentRow.id;
                        const color = getColorFromStatus(row.semaforo);
                        return (
                          <Paper
                            key={row.id}
                            onClick={() => {
                              if (currentRow && currentRow.id === row.id) {
                                setPreviewLoading(false); 
                                return;
                              }
                              startTransition(() => {
                                setCurrentRow(row);
                              });
                            }}
                            sx={{
                              p: 1.5,
                              bgcolor: active
                                ? "rgba(255,255,255,0.18)"
                                : "transparent",
                              cursor: "pointer",
                              borderRadius: "0",
                              borderBottom: "1px solid #eee",
                              display: "flex",
                              flexDirection: "row",
                              alignItems: "flex-start",
                              alignItems: "center",
                              "&:hover": {
                                bgcolor: "rgba(209, 228, 246, 1)",
                              },
                            }}
                            elevation={0}
                          >
                            <Checkbox
                              checked={checked}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCheckboxClick(row.id);
                              }}
                              sx={{ p: 0.5, mr: 1 }}
                            />

                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 700 }}
                              >
                                Folio {row.folio}
                              </Typography>

                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                                sx={{ mt: 0.5 }}
                              >
                                <FiberManualRecordIcon
                                  sx={{
                                    fontSize: "0.9rem",
                                    color: color,
                                  }}
                                />
                                <Typography variant="caption">
                                  Periodos:{" "}
                                  {formatPeriodos(row.periodos || [])}
                                </Typography>
                              </Stack>

                              <Typography variant="caption">
                                Estado: {row.estado}
                              </Typography>
                            </Box>

                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                downloadPDF(row.ruta_url);
                              }}
                              sx={{
                                ml: 1,
                              }}
                            >
                              <PictureAsPdfIcon fontSize="small" />
                            </IconButton>
                          </Paper>
                        );
                      })}
                    </Box>
                  </Paper>

                  {/* PANEL DERECHO: PREVIEW */}
                  <Box
                    sx={{
                      flex: "1 1 auto",
                      minWidth: 0,
                      maxWidth: "100%",
                      height: "70vh",
                      overflowY: "auto",
                    }}
                  >
                    {isPreviewPending || !currentRow  ? (
                      <Paper
                        sx={{
                          p: 4,
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <CircularProgress />
                        <Typography variant="h6" sx={{ mt: 2 }}>
                          Cargando detalle de contingencia...
                        </Typography>
                      </Paper>
                    ) : (
                      <ContingenciasHiddenPreview
                        id={currentRow.folio}
                        formData={currentRow.datos}
                        typeContingencia={currentRow.tipo_contingencia}
                        textContingencia={""}
                      />
                    )}
                  </Box>
                </Box>
              )}
            </PageBgBlock>
          </>
        )}


      </Box>

      <ModalMassive
        open={open}
        btnText="Ir al listado"
        selected={selected}
        action={action}
        onClick={() => navigate("/contingencias_lista")}
        onClose={handleClose}
      />
    </PageBody>
  );
}
